import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  title: string;
  description: string;
  keywords: string;
  canonicalUrl: string;
  ogType?: string;
  schemaData?: Record<string, unknown>;
}

export default function SEOHead({ title, description, keywords, canonicalUrl, ogType = 'website', schemaData }: SEOHeadProps) {
  const location = useLocation();
  const fullUrl = `https://www.mogutv.com${canonicalUrl}`;

  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('og:title', title, true);
    setMeta('og:description', description, true);
    setMeta('og:type', ogType, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:site_name', '蘑菇视频官网', true);
    setMeta('og:locale', 'zh_CN', true);
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    const schemaId = 'schema-webpage';
    let schemaScript = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!schemaScript) {
      schemaScript = document.createElement('script');
      schemaScript.id = schemaId;
      schemaScript.type = 'application/ld+json';
      document.head.appendChild(schemaScript);
    }
    schemaScript.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description: description,
      url: fullUrl,
      ...(schemaData || {}),
    });

    const breadcrumbSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbId = 'schema-breadcrumb';
    let existingBreadcrumb = document.getElementById(breadcrumbId) as HTMLScriptElement | null;

    if (breadcrumbSegments.length > 0) {
      const items = [{ '@type': 'ListItem' as const, position: 1, name: '首页', item: 'https://www.mogutv.com/' }];
      let currentPath = '';
      breadcrumbSegments.forEach((seg, i) => {
        currentPath += `/${seg}`;
        const routeLabels: Record<string, string> = { category: '分类', article: '文章详情', about: '关于我们', dianying: '电影', dianshiju: '电视剧', zongyi: '综艺', dongman: '动漫', jilupian: '纪录片', duanshipin: '短视频', tiyu: '体育', yinyue: '音乐' };
        items.push({
          '@type': 'ListItem' as const,
          position: i + 2,
          name: routeLabels[seg] || seg,
          item: `https://www.mogutv.com${currentPath}`,
        });
      });

      if (!existingBreadcrumb) {
        existingBreadcrumb = document.createElement('script');
        existingBreadcrumb.id = breadcrumbId;
        existingBreadcrumb.type = 'application/ld+json';
        document.head.appendChild(existingBreadcrumb);
      }
      existingBreadcrumb.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items,
      });
    } else if (existingBreadcrumb) {
      existingBreadcrumb.remove();
    }
  }, [title, description, keywords, canonicalUrl, fullUrl, ogType, schemaData, location.pathname]);

  return null;
}